import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Search, ChevronRight, Copy, Check,
  Book, Code, Zap, Globe, Lock, AlertTriangle,
  RefreshCw, ExternalLink
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const METHOD_COLORS = {
  GET:    'bg-blue-100 text-blue-700',
  POST:   'bg-emerald-100 text-emerald-700',
  PUT:    'bg-amber-100 text-amber-700',
  DELETE: 'bg-red-100 text-red-700',
  PATCH:  'bg-purple-100 text-purple-700',
};

const RESOURCE_GROUPS = [
  { key: 'agents',     label: 'Agents',         pathPrefix: '/agents',     icon: Zap,          desc: 'Register, manage, and inspect AI agents.' },
  { key: 'logs',       label: 'Logs',           pathPrefix: '/logs',       icon: Book,         desc: 'Parse and ingest L7 access logs.' },
  { key: 'shadow_ai',  label: 'Shadow AI',      pathPrefix: '/shadow-ai',  icon: Globe,        desc: 'Detect unauthorized AI SaaS usage.' },
  { key: 'tls',        label: 'TLS',            pathPrefix: '/tls',        icon: Lock,         desc: 'Parse TLS ClientHello and JA3 fingerprints.' },
  { key: 'identity',   label: 'Identity',       pathPrefix: '/identity',   icon: Shield,       desc: 'Resolve users via Okta / Entra ID.' },
  { key: 'orgs',       label: 'Organizations',  pathPrefix: '/orgs',       icon: Shield,       desc: 'Manage orgs, departments, and members.' },
  { key: 'defense',    label: 'Defense',        pathPrefix: '/defense',    icon: AlertTriangle,desc: 'Generate robots.txt, blocklists, honeypots.' },
  { key: 'alerts',     label: 'Alerts',         pathPrefix: '/alerts',     icon: AlertTriangle,desc: 'Email + Slack alert rules and digests.' },
  { key: 'verify',     label: 'Verify',         pathPrefix: '/verify',     icon: Shield,       desc: 'rDNS verification, IP ranges, posture analysis.' },
  { key: 'scan',       label: 'Scan',           pathPrefix: '/scan',       icon: Search,       desc: 'Run a privacy scan on any website.' },
  { key: 'report',     label: 'Reports',        pathPrefix: '/report',     icon: Book,         desc: 'Retrieve scan reports.' },
  { key: 'sharecard',  label: 'Share Cards',    pathPrefix: '/sharecard',  icon: ExternalLink, desc: 'Generate shareable summary cards.' },
  { key: 'billing',    label: 'Billing',        pathPrefix: '/billing',    icon: Code,         desc: 'Checkout, portal, entitlements.' },
  { key: 'community',  label: 'Community',      pathPrefix: '/community',  icon: Globe,        desc: 'Domain submissions and signals.' },
  { key: 'leaderboard',label: 'Leaderboard',    pathPrefix: '/leaderboard',icon: Book,         desc: 'AI bot activity rankings.' },
  { key: 'health',     label: 'Health',         pathPrefix: '/health',     icon: Zap,          desc: 'System and scanner health checks.' },
];

function groupForPath(path) {
  for (const g of RESOURCE_GROUPS) {
    if (path.includes(g.pathPrefix)) return g.key;
  }
  return 'other';
}

function CodeBlock({ language, code, label }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="bg-slate-900 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700">
        <span className="text-xs text-slate-400 uppercase tracking-wider">{label || language}</span>
        <button
          onClick={handleCopy}
          className="text-slate-400 hover:text-white transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
      <pre className="text-xs text-slate-200 font-mono p-4 overflow-x-auto">{code}</pre>
    </div>
  );
}

function buildCurlSample(method, path, requestBody) {
  const url = `https://api.agentcheck.com${path}`;
  const parts = [`curl -X ${method} '${url}'`];
  parts.push("  -H 'Content-Type: application/json'");
  if (requestBody && method !== 'GET' && method !== 'DELETE') {
    parts.push(`  -d '${JSON.stringify(requestBody, null, 2).replace(/\n/g, "\n     ")}'`);
  }
  return parts.join(' \\\n');
}

function buildPythonSample(method, path, requestBody) {
  const url = `https://api.agentcheck.com${path}`;
  const lines = ['import requests', ''];
  if (requestBody && method !== 'GET' && method !== 'DELETE') {
    lines.push(`payload = ${JSON.stringify(requestBody, null, 4)}`);
    lines.push('');
    lines.push(`response = requests.${method.toLowerCase()}(`);
    lines.push(`    "${url}",`);
    lines.push(`    json=payload,`);
    lines.push(`)`);
  } else {
    lines.push(`response = requests.${method.toLowerCase()}("${url}")`);
  }
  lines.push('print(response.json())');
  return lines.join('\n');
}

function buildNodeSample(method, path, requestBody) {
  const url = `https://api.agentcheck.com${path}`;
  const lines = ["const fetch = require('node-fetch');", ''];
  lines.push(`async function call() {`);
  if (requestBody && method !== 'GET' && method !== 'DELETE') {
    lines.push(`  const res = await fetch("${url}", {`);
    lines.push(`    method: "${method}",`);
    lines.push(`    headers: { "Content-Type": "application/json" },`);
    lines.push(`    body: JSON.stringify(${JSON.stringify(requestBody, null, 2).replace(/\n/g, "\n    ")}),`);
    lines.push(`  });`);
  } else {
    lines.push(`  const res = await fetch("${url}");`);
  }
  lines.push(`  const data = await res.json();`);
  lines.push(`  console.log(data);`);
  lines.push(`}`);
  lines.push('call();');
  return lines.join('\n');
}

function buildSampleBody(parameters, schemas) {
  if (!parameters?.length && !schemas) return null;
  const body = {};
  // Try to infer from requestBody schemas if available
  const rbSchema = schemas?.requestBody;
  if (rbSchema?.properties) {
    for (const [k, prop] of Object.entries(rbSchema.properties)) {
      body[k] = exampleForType(prop);
    }
    return body;
  }
  return null;
}

function exampleForType(prop) {
  if (prop.example !== undefined) return prop.example;
  if (prop.default !== undefined) return prop.default;
  if (prop.enum?.length) return prop.enum[0];
  switch (prop.type) {
    case 'string':  return prop.format === 'email' ? 'user@example.com' : 'string';
    case 'integer':
    case 'number':  return 0;
    case 'boolean': return false;
    case 'array':   return prop.items ? [exampleForType(prop.items)] : [];
    case 'object':  return {};
    default:        return null;
  }
}

function EndpointCard({ endpoint, spec }) {
  const [lang, setLang] = useState('curl');
  const method = endpoint.method.toUpperCase();
  const body = useMemo(() => {
    const op = endpoint.operation || {};
    let schema = null;
    if (op.requestBody?.content?.['application/json']?.schema) {
      const s = op.requestBody.content['application/json'].schema;
      schema = s.$ref ? resolveSchema(s.$ref, spec) : s;
    }
    return buildSampleBody(op.parameters, schema ? { requestBody: schema } : null);
  }, [endpoint, spec]);

  const sample = useMemo(() => {
    if (lang === 'curl')   return buildCurlSample(method, endpoint.path, body);
    if (lang === 'python') return buildPythonSample(method, endpoint.path, body);
    if (lang === 'node')   return buildNodeSample(method, endpoint.path, body);
    return '';
  }, [lang, method, endpoint.path, body]);

  const op = endpoint.operation || {};
  const summary = op.summary || op.description || endpoint.path;
  const description = op.description || '';
  const parameters = (op.parameters || []).filter(p => p.in === 'path' || p.in === 'query');

  return (
    <div id={endpoint.id} className="scroll-mt-24 border-t border-slate-200 pt-12 mt-12 first:border-t-0 first:pt-0 first:mt-0">
      <div className="flex items-start gap-3 mb-4">
        <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${METHOD_COLORS[method] || 'bg-slate-100 text-slate-700'}`}>
          {method}
        </span>
        <code className="text-sm font-mono text-slate-900 bg-slate-100 px-3 py-1 rounded">{endpoint.path}</code>
      </div>

      <h3 className="text-2xl font-bold text-slate-900 mb-2">{summary}</h3>
      {description && <p className="text-slate-600 mb-6">{description}</p>}

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          {parameters.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wider">Parameters</h4>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-slate-700">Name</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-700">In</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-700">Type</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-700">Required</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parameters.map((p) => (
                      <tr key={`${p.name}-${p.in}`} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-mono text-xs text-slate-900">{p.name}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{p.in}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{p.schema?.type || 'string'}</td>
                        <td className="px-3 py-2 text-xs">
                          {p.required
                            ? <span className="text-red-600 font-medium">yes</span>
                            : <span className="text-slate-500">no</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {body && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wider">Request Body</h4>
              <CodeBlock language="json" label="JSON" code={JSON.stringify(body, null, 2)} />
            </div>
          )}

          {op.responses?.['200'] && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wider">Response</h4>
              <div className="text-xs text-slate-600">
                {op.responses['200'].description || 'Success'}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="flex gap-1 mb-2">
            {['curl', 'python', 'node'].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  lang === l ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {l === 'curl' ? 'cURL' : l === 'python' ? 'Python' : 'Node.js'}
              </button>
            ))}
          </div>
          <CodeBlock language={lang} code={sample} />
        </div>
      </div>
    </div>
  );
}

function resolveSchema(ref, spec) {
  // ref like "#/components/schemas/Foo"
  const parts = ref.replace(/^#\//, '').split('/');
  let cur = spec;
  for (const p of parts) {
    if (!cur || !cur[p]) return null;
    cur = cur[p];
  }
  return cur;
}

export default function ApiDocsPage() {
  const [spec, setSpec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('agents');

  useEffect(() => {
    api.getOpenAPISpec()
      .then(setSpec)
      .catch(err => setError(err.message || 'Failed to load API spec'))
      .finally(() => setLoading(false));
  }, []);

  const endpoints = useMemo(() => {
    if (!spec?.paths) return [];
    const out = [];
    for (const [path, methods] of Object.entries(spec.paths)) {
      for (const [method, operation] of Object.entries(methods)) {
        if (!['get','post','put','delete','patch'].includes(method)) continue;
        const id = `${method}-${path}`.replace(/[^a-z0-9-]/gi, '-').toLowerCase();
        out.push({
          id,
          method,
          path,
          operation,
          group: groupForPath(path),
        });
      }
    }
    return out;
  }, [spec]);

  const filteredEndpoints = useMemo(() => {
    let list = endpoints.filter(e => e.group === selectedGroup);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(e =>
        e.path.toLowerCase().includes(q) ||
        (e.operation.summary || '').toLowerCase().includes(q) ||
        (e.operation.description || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [endpoints, selectedGroup, search]);

  const allFiltered = useMemo(() => {
    if (!search) return null;
    const q = search.toLowerCase();
    return endpoints.filter(e =>
      e.path.toLowerCase().includes(q) ||
      (e.operation.summary || '').toLowerCase().includes(q) ||
      (e.operation.description || '').toLowerCase().includes(q)
    );
  }, [endpoints, search]);

  const groupCounts = useMemo(() => {
    const counts = {};
    for (const e of endpoints) {
      counts[e.group] = (counts[e.group] || 0) + 1;
    }
    return counts;
  }, [endpoints]);

  const selectedGroupInfo = RESOURCE_GROUPS.find(g => g.key === selectedGroup) || RESOURCE_GROUPS[0];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200 sticky top-0 z-30 bg-white/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#007AFF] flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-slate-900 text-lg">AgentCheck</span>
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/" className="text-sm text-slate-600 hover:text-slate-900">Site Scan</Link>
            <Link to="/agents" className="text-sm text-slate-600 hover:text-slate-900">Agent Control Plane</Link>
            <span className="text-sm font-medium text-slate-900">API Docs</span>
          </nav>
        </div>
      </header>

      <div className="max-w-7xl mx-auto flex gap-8 px-4 py-8">
        {/* Sidebar */}
        <aside className="w-64 flex-shrink-0 sticky top-20 h-[calc(100vh-6rem)] overflow-y-auto">
          <div className="mb-6">
            <h1 className="text-xl font-bold text-slate-900 mb-1">API Reference</h1>
            <p className="text-xs text-slate-500">
              {spec?.info?.version || 'v1'} · {endpoints.length} endpoints
            </p>
          </div>

          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              className="pl-9 bg-white border-slate-200"
              placeholder="Search endpoints..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <nav className="space-y-0.5">
            {RESOURCE_GROUPS.map((g) => {
              const count = groupCounts[g.key] || 0;
              if (count === 0) return null;
              const active = selectedGroup === g.key && !search;
              return (
                <button
                  key={g.key}
                  onClick={() => { setSelectedGroup(g.key); setSearch(''); }}
                  className={`w-full flex items-center justify-between px-3 py-1.5 rounded-md text-sm transition-colors ${
                    active ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <g.icon className="w-3.5 h-3.5" />
                    {g.label}
                  </span>
                  <span className="text-xs text-slate-400">{count}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0">
          {loading && (
            <div className="text-center py-20">
              <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
              <p className="text-slate-500">Loading OpenAPI spec...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-20">
              <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-2">Could not load API spec</p>
              <p className="text-sm text-slate-500">{error}</p>
            </div>
          )}

          {spec && (
            <>
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-2">
                  <selectedGroupInfo.icon className="w-6 h-6 text-[#007AFF]" />
                  <h2 className="text-3xl font-bold text-slate-900">
                    {search ? `Search: "${search}"` : selectedGroupInfo.label}
                  </h2>
                </div>
                <p className="text-slate-600">
                  {search ? `${allFiltered?.length || 0} matching endpoints` : selectedGroupInfo.desc}
                </p>
              </div>

              {/* Quick info banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-12">
                <div className="grid sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700 font-semibold block mb-1">Base URL</span>
                    <code className="text-xs text-slate-700 bg-white px-2 py-1 rounded">https://api.agentcheck.com</code>
                  </div>
                  <div>
                    <span className="text-blue-700 font-semibold block mb-1">Auth</span>
                    <span className="text-xs text-slate-700">No key required for public endpoints. Agent SDK uses API keys.</span>
                  </div>
                  <div>
                    <span className="text-blue-700 font-semibold block mb-1">Rate Limit</span>
                    <span className="text-xs text-slate-700">20 req/hour per IP</span>
                  </div>
                </div>
              </div>

              {(search ? allFiltered : filteredEndpoints).map(e => (
                <EndpointCard key={e.id} endpoint={e} spec={spec} />
              ))}

              {(search ? allFiltered : filteredEndpoints)?.length === 0 && (
                <div className="text-center py-20 text-slate-500">
                  No endpoints match.
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
